import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout,
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  Spinner,
  InlineStack,
  BlockStack,
  Text,
  Checkbox,
  Divider,
  Badge
} from '@shopify/polaris';
import { useAuth } from '../components/AuthProvider';
import { authService } from '../services/authService';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3000';

const AdminSettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    shippingProvider: 'bosta',
    whatsappAutoSend: false,
    whatsappProductId: '',
    whatsappPhoneId: '',
    whatsappApiToken: ''
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [notification, setNotification] = useState(null);

  const isAdmin = user?.group?.toLowerCase() === 'super admin';

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/settings`, {
        headers: authService.getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        const settingsData = {
          shippingProvider: data.shipping?.provider || 'bosta',
          whatsappAutoSend: data.whatsapp?.autoSend || false,
          whatsappProductId: data.whatsapp?.productId || '',
          whatsappPhoneId: data.whatsapp?.phoneId || '',
          whatsappApiToken: data.whatsapp?.apiToken || ''
        };
        setSettings(settingsData);
        setOriginalSettings(settingsData);
      } else {
        throw new Error('Failed to load settings');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setNotification({
        type: 'critical',
        message: 'Failed to load admin settings. Using defaults.'
      });
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          shipping: { provider: settings.shippingProvider },
          whatsapp: {
            autoSend: settings.whatsappAutoSend,
            productId: settings.whatsappProductId,
            phoneId: settings.whatsappPhoneId,
            apiToken: settings.whatsappApiToken
          }
        })
      });

      if (response.ok) {
        setOriginalSettings({ ...settings });
        setNotification({ type: 'success', message: 'Settings saved successfully!' });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setNotification({ type: 'critical', message: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  useEffect(() => {
    if (isAdmin) {
      loadSettings();
    } else {
      setLoading(false);
    }
  }, [isAdmin, loadSettings]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  if (!isAdmin) {
    return (
      <Layout>
        <Layout.Section>
          <Banner status="critical">
            <p>Access denied. You need administrator privileges to view this page.</p>
          </Banner>
        </Layout.Section>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <InlineStack align="center" gap="400">
              <Spinner size="large" />
              <Text>Loading admin settings...</Text>
            </InlineStack>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  const isBosta = settings.shippingProvider === 'bosta';
  const isShippingCompany = settings.shippingProvider === 'shipping_company';

  return (
    <Layout>
      {notification && (
        <Layout.Section>
          <Banner
            status={notification.type}
            onDismiss={() => setNotification(null)}
          >
            <p>{notification.message}</p>
          </Banner>
        </Layout.Section>
      )}

      {/* ======= SHIPPING PROVIDER SECTION ======= */}
      <Layout.Section>
        <Card sectioned>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingMd" as="h2">Shipping Provider</Text>
              <Badge status={isBosta ? 'info' : 'success'}>
                {isBosta ? 'Bosta' : 'Shipping Company App'}
              </Badge>
            </InlineStack>
            
            <Text color="subdued">
              Select which shipping provider to use for automatic order delivery creation. Only the selected provider will be used.
            </Text>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {/* Bosta Option */}
              <div
                onClick={() => handleInputChange('shippingProvider', 'bosta')}
                style={{
                  flex: '1',
                  minWidth: '200px',
                  padding: '20px',
                  border: `2px solid ${isBosta ? '#2c6ecb' : '#e1e3e5'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  backgroundColor: isBosta ? '#f0f6ff' : '#ffffff',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
              >
                {isBosta && (
                  <div style={{
                    position: 'absolute', top: '10px', right: '10px',
                    width: '24px', height: '24px', borderRadius: '50%',
                    backgroundColor: '#2c6ecb', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '14px', fontWeight: 'bold'
                  }}>✓</div>
                )}
                <div style={{ marginBottom: '8px' }}>
                  <Text variant="headingMd" as="h3">📦 Bosta</Text>
                </div>
                <Text color="subdued">
                  Use Bosta shipping service for deliveries. Supports COD, prepaid, and AWB label generation.
                </Text>
              </div>

              {/* Shipping Company Option */}
              <div
                onClick={() => handleInputChange('shippingProvider', 'shipping_company')}
                style={{
                  flex: '1',
                  minWidth: '200px',
                  padding: '20px',
                  border: `2px solid ${isShippingCompany ? '#008060' : '#e1e3e5'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  backgroundColor: isShippingCompany ? '#f0fdf4' : '#ffffff',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
              >
                {isShippingCompany && (
                  <div style={{
                    position: 'absolute', top: '10px', right: '10px',
                    width: '24px', height: '24px', borderRadius: '50%',
                    backgroundColor: '#008060', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '14px', fontWeight: 'bold'
                  }}>✓</div>
                )}
                <div style={{ marginBottom: '8px' }}>
                  <Text variant="headingMd" as="h3">🚚 Shipping Company App</Text>
                </div>
                <Text color="subdued">
                  Use your custom Shipping Company App for deliveries. Supports COD, prepaid, and custom tracking.
                </Text>
              </div>
            </div>

            {settings.shippingProvider !== originalSettings.shippingProvider && (
              <Banner status="warning">
                <p>
                  Changing the shipping provider will affect all <strong>new</strong> orders. Existing orders already synced will not be affected.
                </p>
              </Banner>
            )}

            {hasChanges && (
              <InlineStack align="end">
                <Button primary onClick={saveSettings} loading={saving}>
                  Save Changes
                </Button>
              </InlineStack>
            )}
          </BlockStack>
        </Card>
      </Layout.Section>

      {/* ======= WHATSAPP SECTION ======= */}
      <Layout.Section>
        <Card sectioned>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingMd" as="h2">WhatsApp Integration Settings</Text>
              <Badge status={settings.whatsappAutoSend ? 'success' : 'attention'}>
                {settings.whatsappAutoSend ? 'Auto-send Enabled' : 'Manual Mode'}
              </Badge>
            </InlineStack>
            
            <Text color="subdued">
              Configure WhatsApp notification behavior and API credentials.
            </Text>

            <FormLayout>
              <Checkbox
                label="Enable automatic WhatsApp notifications"
                helpText="When enabled, WhatsApp notifications will be sent automatically when previews are ready."
                checked={settings.whatsappAutoSend}
                onChange={(value) => handleInputChange('whatsappAutoSend', value)}
              />

              <Divider />

              <Text variant="headingSm" as="h3">API Configuration</Text>
              
              <TextField
                label="WhatsApp Product ID"
                value={settings.whatsappProductId}
                onChange={(value) => handleInputChange('whatsappProductId', value)}
                placeholder="Enter your Maytapi product ID"
              />

              <TextField
                label="WhatsApp Phone ID"
                value={settings.whatsappPhoneId}
                onChange={(value) => handleInputChange('whatsappPhoneId', value)}
                placeholder="Enter your Maytapi phone ID"
              />

              <TextField
                label="WhatsApp API Token"
                type="password"
                value={settings.whatsappApiToken}
                onChange={(value) => handleInputChange('whatsappApiToken', value)}
                placeholder="Enter your Maytapi API token"
              />
            </FormLayout>
          </BlockStack>
        </Card>
      </Layout.Section>

      {/* ======= INFO SIDEBAR ======= */}
      <Layout.Section secondary>
        <Card sectioned>
          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">Shipping Provider Info</Text>
            <Text color="subdued">
              {isBosta
                ? 'Bosta is currently active. All new orders will automatically create Bosta deliveries.'
                : 'Shipping Company App is currently active. All new orders will create deliveries via the custom app.'}
            </Text>
            <BlockStack gap="100">
              <Text>• Automatic tracking number assignment</Text>
              <Text>• COD/Prepaid detection</Text>
              <Text>• AWB label generation</Text>
              <Text>• Webhook status updates</Text>
            </BlockStack>
          </BlockStack>
        </Card>

        <Card sectioned>
          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">Security Notes</Text>
            <Text color="subdued">
              • Only super admins can modify these settings
            </Text>
            <Text color="subdued">
              • Changes are logged for audit purposes
            </Text>
            <Text color="subdued">
              • Test your configuration after making changes
            </Text>
          </BlockStack>
        </Card>
      </Layout.Section>
    </Layout>
  );
};

export default AdminSettingsPage;
